/**
 * Declarative conditional-display wrappers for the admin frontend.
 *
 * Wraps the hooks in ``use-entity-registration.ts`` so JSX can say::
 *
 *   <IfEntity name="lab_analysis">
 *     <LabPanel />
 *   </IfEntity>
 *
 *   <IfEntityAttribute name="lab_analysis" attribute="sample_condition">
 *     <SampleTrackingCard />
 *   </IfEntityAttribute>
 *
 *   <IfAnyEntity names={["lab_analysis", "inspection"]}>
 *     <AnalysisTabsHeader />
 *   </IfAnyEntity>
 *
 * Phase 4 of the tenant-agnostic claims migration introduces these so
 * panels, cards, tabs, and fields render based on entity registration
 * (tenant configuration) instead of ``if (tenant.slug === 'oc')``
 * branching.
 */

import { type ReactNode } from 'react'

import {
  useAnyEntityRegistered,
  useEntityAttribute,
  useEntityRegistered,
} from '@/admin/hooks/use-entity-registration'

interface IfEntityProps {
  name: string
  children: ReactNode
  fallback?: ReactNode
}

export function IfEntity({ name, children, fallback = null }: IfEntityProps) {
  const registered = useEntityRegistered(name)
  return registered ? <>{children}</> : <>{fallback}</>
}

interface IfEntityAttributeProps {
  name: string
  attribute: string
  children: ReactNode
  fallback?: ReactNode
}

export function IfEntityAttribute({
  name,
  attribute,
  children,
  fallback = null,
}: IfEntityAttributeProps) {
  const present = useEntityAttribute(name, attribute)
  return present ? <>{children}</> : <>{fallback}</>
}

interface IfAnyEntityProps {
  names: string[]
  children: ReactNode
  fallback?: ReactNode
}

export function IfAnyEntity({ names, children, fallback = null }: IfAnyEntityProps) {
  const anyRegistered = useAnyEntityRegistered(names)
  return anyRegistered ? <>{children}</> : <>{fallback}</>
}
